/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { v4 } from 'uuid';
import { withI18n } from '@shopify/react-i18n';
import { TextStyle, SkeletonBodyText } from '@shopify/polaris';
import { store } from 'src/stores/store';
import { connect } from 'react-redux';
import ProductList from 'src/components/views/common/product-list';

type Props = {
  tenantId: string,
  query: string,
  money_format: string,
  filterSettings: Object
};
type ComposedProps = Props & WithI18nProps;

const NUMBER_PRODUCTS_PER_PAGE = 20;

const ProductPerformanceCompare = (props: ComposedProps) => {
  const {
    i18n,
    tenantId,
    query,
    filterSettings,
    externalInfo: { money_format }
  } = props;
  const [withPerformanceRanking, setWithPerformanceRanking] = useState({
    page: 1,
    loading: false,
    result: {}
  });
  const [withoutPerformanceRanking, setWithoutPerformanceRanking] = useState({
    page: 1,
    loading: false,
    result: {}
  });

  const sortingAvailableFirst = _.get(filterSettings, 'general.sortingAvailableFirst', false);
  const availableAfterFiltering = _.get(filterSettings, 'general.availableAfterFiltering', false);
  const productAndVariantAvailable = _.get(
    filterSettings,
    'general.productAndVariantAvailable',
    false
  );
  const host = process.env.REACT_APP_ADMIN_V2_BASE_URL || 'https://staging.bc-solutions.net';
  let previewInterval = null;
  let queryWithPerformanceRanking = null;
  let queryWithoutPerformanceRanking = null;
  // Call api get products
  useEffect(() => {
    setWithPerformanceRanking({ ...withPerformanceRanking, loading: true });
    setWithoutPerformanceRanking({ ...withoutPerformanceRanking, loading: true });
    previewInterval = setTimeout(() => {
      queryWithPerformanceRanking = queryProducts(1, true);
      queryWithoutPerformanceRanking = queryProducts(1, false);
      queryWithPerformanceRanking.then(res => {
        setWithPerformanceRanking({
          page: 1,
          loading: false,
          result: res,
          products: res.products
        });
      });
      queryWithoutPerformanceRanking.then(res => {
        setWithoutPerformanceRanking({
          page: 1,
          loading: false,
          result: res,
          products: res.products
        });
      });
    }, 500);
    return () => clearTimeout(previewInterval);
  }, [query]);

  const queryProducts = async (page, hasRanking) => {
    const queries = {
      shop: tenantId,
      t: Date.now(),
      page: page,
      limit: NUMBER_PRODUCTS_PER_PAGE,
      q: query,
      sort: 'relevance',
      collection_scope: 0,
      product_available: availableAfterFiltering || productAndVariantAvailable,
      variant_available: productAndVariantAvailable,
      reset_cache: true,
      event_type: 'init',
      sid: v4(),
      rid: v4(),
      ignore_performance_ranking: !hasRanking
    };
    if (sortingAvailableFirst) {
      _.set(queries, 'sort_first', 'available');
    }
    const params = new URLSearchParams(queries).toString();

    const currentState = store.getState();
    const { authToken } = currentState.oauth;
    return await fetch(`${host}/bc-sf-filter/search?${params}`, {
      method: 'GET', // or 'PUT'
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken
      }
    }).then(async r => {
      const results = await r.json();
      if (!hasRanking) return results;
      const productList = results.products || [];
      const rankingList = _.get(results, 'test_search_data.performance_ranking', []);
      const products = productList.map(product => {
        const hasPosChange = rankingList.find(pos => `${pos.item}` === `${product.id}`);
        if (hasPosChange) {
          return {
            ...product,
            posChange: _.get(hasPosChange, 'posChange', 0)
          };
        }
        return product;
      });
      _.set(results, 'products', products);
      return results;
    });
  };

  // Load more
  const onLoadMore = hasRanking => {
    const data = hasRanking ? withPerformanceRanking : withoutPerformanceRanking;
    const setData = hasRanking ? setWithPerformanceRanking : setWithoutPerformanceRanking;
    setData({ ...data, loading: true });
    const nextPage = data.page + 1;
    queryProducts(nextPage, hasRanking).then(res => {
      setData({
        page: nextPage,
        loading: false,
        result: res,
        products: [...data.products, ...res.products]
      });
    });
  };

  // Render product list
  const renderProductList = hasRanking => {
    const queryData = hasRanking ? withPerformanceRanking : withoutPerformanceRanking;
    return (
      <>
        <div className="header">
          <span>
            {i18n.translate('AdminHTML.TestYourSearch.Table.CompareView.column_Label', {
              t: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    `AdminHTML.TestYourSearch.Table.CompareView.${hasRanking ? 'with' : 'without'}`
                  )}
                </TextStyle>
              )
            })}
          </span>
          {!!queryData.result.total_product && (
            <span>
              {i18n.translate(`AdminHTML.Common.Duration.result`, {
                count: queryData.result.total_product,
                nt_number: i18n.formatNumber(queryData.result.total_product)
              })}
            </span>
          )}
        </div>
        <div className="Polaris-DataTable__ScrollContainer">
          {queryData.loading ? (
            <div style={{ padding: '10px 8px 18px' }}>
              {[1, 2, 3, 4, 5, 6].map(number => (
                <div style={{ marginTop: '8px' }} key={`loading-${number}`}>
                  <SkeletonBodyText />
                  <SkeletonBodyText />
                  <SkeletonBodyText />
                </div>
              ))}
            </div>
          ) : (
            <ProductList
              type="ranking"
              products={queryData.products}
              hasRanking={hasRanking}
              moneyFormat={money_format}
              onloadMore={() => onLoadMore(hasRanking)}
              loading={queryData.loading}
              page={queryData.page}
              perPage={NUMBER_PRODUCTS_PER_PAGE}
              hideLoadMore={
                queryData.page * NUMBER_PRODUCTS_PER_PAGE >= (queryData.result.total_product || 0)
              }
            />
          )}
        </div>
      </>
    );
  };

  if (
    !_.get(withoutPerformanceRanking, 'result.total_product', 0) &&
    !withoutPerformanceRanking.loading
  )
    return null;
  return (
    <div className="product-compare">
      <div className="product-list">
        <div className="with-ranking">{renderProductList(true)}</div>
        <div className="without-ranking">{renderProductList(false)}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const externalInfo = _.get(state.oauth, 'tenantApp.externalInfo', {});
  return {
    tenantId,
    filterSettings: _.get(state, 'filterSettings.filterSettings', {}),
    externalInfo
  };
};

export default withI18n()(connect(mapStateToProps)(ProductPerformanceCompare));
